import ReactSelect, { FormatOptionLabelMeta } from "react-select"
import CreatableSelect from "react-select/creatable"

export interface FilterOption {
    value: string
    label: string
}

interface FilterMultiSelectProps {
    /** Доступное имя: в фильтрах нет видимых подписей, только placeholder'ы. */
    ariaLabel: string
    placeholder: string
    options: readonly FilterOption[]
    values: string[]
    /**
     * Разрешает значение вне списка. Включается только там, где сервер
     * принимает произвольную строку: для перечислимого фильтра свободный
     * ввод дал бы не пустую выдачу, а отказ контракта.
     */
    isCreatable?: boolean
    onChange: (values: string[]) => void
}

/**
 * В меню под русским названием показываем технический идентификатор — тот же,
 * что стоит в таблице и в детали события. Без него выбранный фильтр не с чем
 * сопоставить глазами. В самом чипе идентификатор не нужен: там уже видно,
 * что выбрано, а вторая строка ломала бы высоту поля.
 */
const formatOptionLabel = (
    option: FilterOption,
    meta: FormatOptionLabelMeta<FilterOption>
) => {
    if (meta.context !== "menu" || option.label === option.value) return option.label

    return (
        <span className="block">
            <span className="block">{option.label}</span>
            <span className="block text-xs text-[#828282]">{option.value}</span>
        </span>
    )
}

export const FilterMultiSelect = ({
    ariaLabel,
    placeholder,
    options,
    values,
    isCreatable = false,
    onChange,
}: FilterMultiSelectProps) => {
    // Значение, которого нет в списке вариантов (добавлено вручную или
    // осталось от контракта прошлой версии), показываем как есть — иначе
    // выбранный фильтр молча исчез бы с экрана, продолжая влиять на выдачу.
    const selected = values.map(
        (value) => options.find((option) => option.value === value) ?? { value, label: value }
    )

    const shared = {
        isMulti: true as const,
        "aria-label": ariaLabel,
        value: selected,
        options,
        placeholder,
        formatOptionLabel,
        className: "flex min-h-[52px] w-full items-center rounded-[26px] bg-white px-4 py-1 text-color-dark",
        classNamePrefix: "react_select_custom",
        classNames: {
            control: () => "!min-h-0 w-full !border-0 !p-0 !shadow-none",
            valueContainer: () => "!p-0",
        },
        onChange: (selectedOptions: readonly FilterOption[]) => onChange(
            selectedOptions.map((option) => option.value.trim()).filter(Boolean)
        ),
    }

    if (!isCreatable) {
        return <ReactSelect<FilterOption, true> {...shared} noOptionsMessage={() => "Нет опций"} />
    }

    return (
        <CreatableSelect<FilterOption, true>
            {...shared}
            formatCreateLabel={(input) => `Добавить «${input}»`}
        />
    )
}
